/**
 * Shared helpers to surface the first invalid form field after a failed save.
 *
 * Intended usage (any form):
 * 1. Mark submitted / run validation so fields paint `aria-invalid` / `data-error`
 * 2. Reveal any form-specific hidden UI (accordion, tab, nav panel) if needed
 * 3. Call `scrollToFirstInvalidField({ fields: getInvalidFieldNames(validator) })`
 *
 * Keep container reveal in each form; this util only scrolls and focuses.
 */

const FOCUSABLE_SELECTOR =
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const INVALID_SELECTOR = '[aria-invalid="true"], [data-error]';

const escapeAttributeValue = value =>
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
        ? CSS.escape(value)
        : String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

/**
 * @param {Record<string, { isInvalid?: boolean }> | null | undefined} validator
 * @returns {string[]}
 */
export function getInvalidFieldNames(validator) {
    if (!validator || typeof validator !== "object") {
        return [];
    }

    return Object.keys(validator).filter(
        key => key !== "isValid" && validator[key]?.isInvalid,
    );
}

/**
 * Prefer a focusable control; fall back to the error-marked node.
 * @param {Element} el
 * @returns {HTMLElement | null}
 */
function resolveFocusTarget(el) {
    if (!(el instanceof Element)) {
        return null;
    }

    if (el.matches(FOCUSABLE_SELECTOR)) {
        return /** @type {HTMLElement} */ (el);
    }

    const nested = el.querySelector(FOCUSABLE_SELECTOR);
    if (nested instanceof HTMLElement) {
        return nested;
    }

    return el instanceof HTMLElement ? el : null;
}

/**
 * @param {{ root?: ParentNode | null, fields?: string[] }} [options]
 * @returns {{ element: HTMLElement, field?: string } | null}
 */
export function findInvalidFieldElement({ root = document, fields = [] } = {}) {
    const scope =
        root && typeof root.querySelector === "function" ? root : document;

    // `fields` is a filter of invalid names; pick the earliest in document order
    // (not the order of the validator rules array).
    if (fields.length > 0) {
        /** @type {{ element: HTMLElement, field: string, node: Element } | null} */
        let best = null;

        for (const field of fields) {
            if (!field) {
                continue;
            }

            const named = scope.querySelector(
                `[name="${escapeAttributeValue(field)}"]`,
            );
            if (!named) {
                continue;
            }

            const element = resolveFocusTarget(named);
            if (!element) {
                continue;
            }

            if (
                !best ||
                best.node.compareDocumentPosition(named) &
                    Node.DOCUMENT_POSITION_PRECEDING
            ) {
                best = { element, field, node: named };
            }
        }

        if (best) {
            return { element: best.element, field: best.field };
        }
    }

    const invalid = scope.querySelector(INVALID_SELECTOR);
    if (!invalid) {
        return null;
    }

    const element = resolveFocusTarget(invalid);
    if (!element) {
        return null;
    }

    return {
        element,
        field: invalid.getAttribute("name") || undefined,
    };
}

/**
 * Scrolls to and focuses the first invalid field in document / visual order.
 *
 * @param {object} [options]
 * @param {ParentNode | null} [options.root] - Limit the search (form panel root)
 * @param {string[]} [options.fields] - Invalid field `name`s (filter); earliest in DOM wins
 * @param {(element: HTMLElement, field?: string) => void | false} [options.onBeforeScroll]
 *        Reveal hidden parents (accordion/tab). Return `false` to abort.
 * @param {ScrollBehavior} [options.behavior]
 * @param {ScrollLogicalPosition} [options.block]
 * @param {number} [options.revealDelayMs] - Wait after onBeforeScroll for layout
 * @returns {boolean} Whether a target was found and scroll was scheduled
 */
export function scrollToFirstInvalidField({
    root = null,
    fields = [],
    onBeforeScroll,
    behavior = "smooth",
    block = "center",
    revealDelayMs = 50,
} = {}) {
    const found = findInvalidFieldElement({ root, fields });
    if (!found?.element) {
        return false;
    }

    if (onBeforeScroll?.(found.element, found.field) === false) {
        return false;
    }

    window.setTimeout(() => {
        const next =
            findInvalidFieldElement({ root, fields })?.element || found.element;

        next.scrollIntoView({ behavior, block });

        if (typeof next.focus === "function") {
            try {
                next.focus({ preventScroll: true });
            } catch {
                // Some non-focusable fallbacks may throw in older browsers.
            }
        }
    }, revealDelayMs);

    return true;
}
